import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Per-route <head> tag manager.
 * Renders <title>, meta description, canonical, OpenGraph/Twitter cards,
 * and any structured data (JSON-LD) so prerendered HTML ships full SEO
 * metadata for search-engine and AI crawlers on the first byte.
 *
 * Props:
 *   title        — <title> string (also used for og:title / twitter:title)
 *   description  — meta description (also used for og:description / twitter:description)
 *   canonical    — canonical URL (defaults to current path on ctrl.aero)
 *   image        — social preview image URL
 *   jsonLd       — object or array of objects to embed as schema.org JSON-LD
 *   noIndex      — if true, adds <meta name="robots" content="noindex,nofollow">
 */
const DEFAULT_SITE = 'https://ctrl.aero';
const DEFAULT_IMAGE =
  '/images/logo-for-website.png';

const ENTITIES = { amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', nbsp: ' ', ndash: '–', mdash: '—', hellip: '…', rsquo: '’', lsquo: '‘', rdquo: '”', ldquo: '“' };

/** Plain-text meta description: tags stripped, entities decoded, ≤155 chars, ends on a sentence. */
export const metaDescription = (text, max = 155) => {
  if (!text) return '';
  let t = String(text)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)))
    .replace(/&([a-z]+);/gi, (m, e) => ENTITIES[e.toLowerCase()] ?? m)
    .replace(/\s+/g, ' ')
    .trim();
  if (t.length <= max) return t;
  const head = t.slice(0, max);
  const cut = Math.max(head.lastIndexOf('. '), head.lastIndexOf('.'), head.lastIndexOf('! '), head.lastIndexOf('? '));
  if (cut > 40) return head.slice(0, cut + 1).trim();
  return head.slice(0, head.lastIndexOf(' ')).replace(/[,;:\s–—-]+$/, '').trim() + '.';
};

const SEO = ({ title, description, canonical, image, imageWidth, imageHeight, imageAlt, ogType = 'website', jsonLd, noIndex }) => {
  const fullTitle = title || 'ctrl.aero — Ultrasonic Aviation Maintenance Troubleshooting';
  const desc = metaDescription(
    description ||
    'Ultrasonic sensors and acoustic imaging cameras for aviation maintenance. Component troubleshooting at the speed of sound.'
  );
  const canonicalUrl = canonical || (typeof window !== 'undefined'
    ? DEFAULT_SITE + window.location.pathname
    : DEFAULT_SITE);
  const abs = (u) => (u && u.startsWith('/') ? DEFAULT_SITE + u : u);
  const imageUrl = abs(image) || abs(DEFAULT_IMAGE);

  const structured = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* OpenGraph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="ctrl.aero" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      {imageWidth && <meta property="og:image:width" content={String(imageWidth)} />}
      {imageHeight && <meta property="og:image:height" content={String(imageHeight)} />}
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={imageUrl} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {structured.map((obj, i) => (
        <script key={`${obj['@type'] || 'ld'}-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

/** Reusable Organization schema for the homepage / global. */
export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'CTRL Systems, Inc.',
  alternateName: 'ctrl.aero',
  url: DEFAULT_SITE,
  logo: DEFAULT_IMAGE,
  email: 'info@ctrlsys.com',
  telephone: '+1-410-876-5676',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '1004 Littlestown Pike, Suite H',
    addressLocality: 'Westminster',
    addressRegion: 'MD',
    postalCode: '21157',
    addressCountry: 'US',
  },
  sameAs: [],
};

/** Helper to build a Product schema. */
export const productJsonLd = ({ name, description, image, brand, sku, url }) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  image,
  brand: brand ? { '@type': 'Brand', name: brand } : undefined,
  sku,
  url,
});

/**
 * FAQPage schema. Pass an array of `{ question, answer }` where `answer`
 * is plain text (HTML is stripped). Only include questions whose answers
 * are visible on the page — required by Google's FAQPage guidelines.
 */
export const faqJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((it) => ({
    '@type': 'Question',
    name: it.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: it.answer,
    },
  })),
});

/**
 * BreadcrumbList schema. Pass an array of `{ name, url }` in order
 * (root first).
 */
export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

/** CollectionPage schema — good for landing pages that link out to
 *  multiple items (e.g. /news, /support/documentation). */
export const collectionPageJsonLd = ({ name, description, url, items = [] }) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name,
  description,
  url,
  hasPart: items.map((it) => ({
    '@type': it.type || 'Article',
    headline: it.name,
    url: it.url,
    datePublished: it.datePublished,
  })),
});

/** Article schema — for individual news / blog posts. */
export const articleJsonLd = ({ headline, description, image, datePublished, url, author = 'CTRL Systems' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline,
  description,
  image,
  datePublished,
  url,
  author: { '@type': 'Organization', name: author },
  publisher: {
    '@type': 'Organization',
    name: 'CTRL Systems',
    logo: { '@type': 'ImageObject', url: DEFAULT_IMAGE },
  },
});

export default SEO;
